import { useState, useCallback, useLayoutEffect, useRef } from 'react';
import { useAppSelector } from '../redux/hooks';
import { selectPlainSiteContent } from '../redux/slices/siteContentSlice';

interface UseNavSpacingOptions {
  navSelector?: string;
  includeHero?: boolean;
  extraOffset?: number;
}

export const useNavSpacing = (options: UseNavSpacingOptions = {}): number => {
  // Target #main-header first for precise matching
  const { navSelector = '#main-header, header, nav', includeHero = true, extraOffset = 0 } = options;
  
  const siteContent = useAppSelector(selectPlainSiteContent);
  
  // Synchronous initial calculation with a safe fallback of 72px instead of 0
  const [spacing, setSpacing] = useState(() => {
    if (typeof window === 'undefined') return 72;
    const nav = document.querySelector(navSelector);
    if (!nav) return 72;
    const navHeight = (nav as HTMLElement).offsetHeight || 72;
    const hero = includeHero ? (nav.previousElementSibling as HTMLElement | null) : null;
    const heroHeight = hero && (hero.children.length > 0 || hero.textContent?.trim()) ? hero.scrollHeight || 0 : 0;
    return navHeight + heroHeight + extraOffset;
  });

  const navRef = useRef<HTMLElement | null>(null);
  const heroRef = useRef<HTMLElement | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const frameIdRef = useRef<number | undefined>(undefined);
  const resizeTimeoutRef = useRef<number | undefined>(undefined);

  const calculateSpacing = useCallback(() => {
    if (typeof window === 'undefined') return;

    if (frameIdRef.current !== undefined) {
      cancelAnimationFrame(frameIdRef.current);
    }

    frameIdRef.current = requestAnimationFrame(() => {
      const nav = navRef.current ?? document.querySelector(navSelector);
      if (!nav) return;

      const navHeight = (nav as HTMLElement).offsetHeight || 72;
      const hero = includeHero
        ? heroRef.current ?? (nav.previousElementSibling as HTMLElement | null)
        : null;
      const heroHeight =
        hero && (hero.children.length > 0 || hero.textContent?.trim())
          ? hero.scrollHeight || 0
          : 0;

      const total = navHeight + heroHeight + extraOffset;
      
      setSpacing((prev) => (prev !== total ? total : prev));
    });
  }, [navSelector, includeHero, extraOffset]);

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;

    const initializeSpacing = () => {
      const nav = document.querySelector(navSelector);
      if (!nav) {
        setTimeout(initializeSpacing, 50);
        return;
      }

      navRef.current = nav as HTMLElement;
      heroRef.current = includeHero
        ? (nav.previousElementSibling as HTMLElement | null)
        : null;

      observerRef.current = new ResizeObserver(() => {
        calculateSpacing();
      });

      observerRef.current.observe(navRef.current);
      if (heroRef.current) {
        observerRef.current.observe(heroRef.current);
      }

      calculateSpacing();
    };

    initializeSpacing();

    const handleResize = () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      resizeTimeoutRef.current = window.setTimeout(calculateSpacing, 100);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      if (observerRef.current) {
        if (navRef.current) {
          observerRef.current.unobserve(navRef.current);
        }
        if (heroRef.current) {
          observerRef.current.unobserve(heroRef.current);
        }
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      
      window.removeEventListener('resize', handleResize);
      
      if (resizeTimeoutRef.current !== undefined) {
        clearTimeout(resizeTimeoutRef.current);
      }
      if (frameIdRef.current !== undefined) {
        cancelAnimationFrame(frameIdRef.current);
      }
    };
  }, [calculateSpacing, navSelector, includeHero, siteContent]);

  return spacing;
};